/**
 * @file MessagesUserPreference.js
 * @description Card for the Messages - Preferred Destination dashboard.
 */

import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { format } from 'date-fns';
import { DASHBOARD_AS_OF_DATE_FORMAT } from './DashboardUtils';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import './MessagesUserPreference.scss';
import NotEnoughData from './NotEnoughData';
import { getText } from '../../i18n';

const TextIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
        >
            <circle cx="16" cy="16" r="16" fill="#EAECF0" fillOpacity="0.65" />
            <path
                d="M10.6779 19.6738C10.8883 19.8999 10.9936 20.2148 10.9652 20.5326C10.9016 21.2449 10.755 21.9862 10.5668 22.6748C11.9617 22.3293 12.8136 21.9286 13.2008 21.7183C13.4206 21.5989 13.6738 21.5708 13.9118 21.6391C14.5697 21.8282 15.2708 21.9306 16 21.9306C19.9956 21.9306 23 18.9221 23 15.5011C23 12.0801 19.9956 9.07159 16 9.07159C12.0044 9.07159 9 12.0801 9 15.5011C9 17.0743 9.6173 18.5341 10.6779 19.6738ZM10.1849 23.8583C10.1782 23.8598 10.1714 23.8612 10.1647 23.8626C10.0728 23.882 9.97922 23.9011 9.88382 23.9198C9.75 23.9461 9.61264 23.9716 9.47172 23.9964C9.27271 24.0313 9.12037 23.8082 9.19898 23.6092C9.25034 23.4792 9.30097 23.3429 9.3503 23.2016C9.38164 23.1118 9.41245 23.0201 9.44259 22.9266C9.44389 22.9225 9.44518 22.9185 9.44648 22.9145C9.69422 22.1432 9.89596 21.2567 9.96979 20.4305C8.74306 19.1122 8 17.388 8 15.5011C8 11.3584 11.5817 8 16 8C20.4183 8 24 11.3584 24 15.5011C24 19.6438 20.4183 23.0022 16 23.0022C15.1832 23.0022 14.3951 22.8874 13.6528 22.6742C13.1334 22.9563 12.0143 23.4698 10.1849 23.8583Z"
                fill="#14BA6D"
            />
            <path
                d="M12 12.8221C12 12.5262 12.2239 12.2863 12.5 12.2863H19.5C19.7761 12.2863 20 12.5262 20 12.8221C20 13.118 19.7761 13.3579 19.5 13.3579H12.5C12.2239 13.3579 12 13.118 12 12.8221ZM12 15.5011C12 15.2052 12.2239 14.9653 12.5 14.9653H19.5C19.7761 14.9653 20 15.2052 20 15.5011C20 15.797 19.7761 16.0369 19.5 16.0369H12.5C12.2239 16.0369 12 15.797 12 15.5011ZM12 18.1801C12 17.8842 12.2239 17.6443 12.5 17.6443H16.5C16.7761 17.6443 17 17.8842 17 18.1801C17 18.476 16.7761 18.7159 16.5 18.7159H12.5C12.2239 18.7159 12 18.476 12 18.1801Z"
                fill="#14BA6D"
            />
        </svg>
    );
};

const NotificationsIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
        >
            <circle cx="16" cy="16" r="16" fill="#EAECF0" fillOpacity="0.65" />
            <path
                d="M17.4268 23.2754C17.2281 23.9324 16.649 24.3867 16 24.3867C15.351 24.3867 14.7719 23.9324 14.5732 23.2754H17.4268ZM16 8.5C16.2511 8.5 16.5 8.72318 16.5 9.05566C16.5 9.07526 16.4988 9.09446 16.4971 9.11328L16.4561 9.55664L16.8906 9.64941C18.9315 10.0844 20.4998 11.9999 20.5 14.332C20.5 14.9486 20.6294 16.5648 20.9473 18.1621C21.1065 18.9623 21.3166 19.7773 21.5898 20.4678C21.693 20.7284 21.8114 20.9835 21.9453 21.2197H10.0547C10.1886 20.9835 10.307 20.7284 10.4102 20.4678C10.6834 19.7773 10.8935 18.9623 11.0527 18.1621C11.3706 16.5648 11.5 14.9486 11.5 14.332C11.5002 11.9999 13.0685 10.0844 15.1094 9.64941L15.5439 9.55664L15.5029 9.11328C15.5012 9.09446 15.5 9.07526 15.5 9.05566C15.5 8.72318 15.7489 8.5 16 8.5Z"
                stroke="#667085"
            />
        </svg>
    );
};

const EmailIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
        >
            <circle cx="16" cy="16" r="16" fill="#EAECF0" fillOpacity="0.65" />
            <path
                d="M8 12.1108C8 10.945 8.89543 10 10 10H22C23.1046 10 24 10.945 24 12.1108V20.554C24 21.7197 23.1046 22.6648 22 22.6648H10C8.89543 22.6648 8 21.7197 8 20.554V12.1108ZM10 11.0554C9.44772 11.0554 9 11.5279 9 12.1108V12.3397L16 16.7724L23 12.3397V12.1108C23 11.5279 22.5523 11.0554 22 11.0554H10ZM23 13.5705L18.2919 16.5518L23 19.6096V13.5705ZM22.9662 20.8269L17.3258 17.1636L16 18.0032L14.6742 17.1636L9.03376 20.8269C9.14774 21.2775 9.53715 21.6094 10 21.6094H22C22.4628 21.6094 22.8523 21.2775 22.9662 20.8269ZM9 19.6096L13.7081 16.5518L9 13.5705V19.6096Z"
                fill="#1366FF"
            />
        </svg>
    );
};

const mapApiResponseToMessagesUserPreferenceData = dataFromApi => {
    const counts = {
        Text: 0,
        Notifications: 0,
        Email: 0,
    };

    dataFromApi.forEach(({ notificationPreferredMethodName, count }) => {
        switch (notificationPreferredMethodName) {
            case 'IndividualEmail':
            case 'UserEmail':
                counts.Email += count;
                break;
            case 'IndividualSMS':
            case 'UserSMS':
                counts.Text += count;
                break;
            case 'PushNotification':
                counts.Notifications += count;
                break;
            default:
                break;
        }
    });

    return [
        { name: 'Text', value: counts.Text, color: '#8EECA2' },
        {
            name: 'Notifications',
            value: counts.Notifications,
            color: '#C4C4C4',
        },
        { name: 'Email', value: counts.Email, color: '#1890FF' },
    ];
};

const renderIconLabel = ({ cx, cy, midAngle, outerRadius, payload }) => {
    if (payload && payload?.value !== 0) {
        const iconMap = {
            Text: <TextIcon />,
            Notifications: <NotificationsIcon />,
            Email: <EmailIcon />,
        };
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 2;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <foreignObject x={x - 16} y={y - 16} width={32} height={32}>
                {iconMap[payload.payload.name]}
            </foreignObject>
        );
    }
    return null;
};

const isMessagesUserPreferenceDataEmpty = data => {
    if (!Array.isArray(data) || data.length === 0) return true;
    // Filter out the "None" user preference
    const mappedMessagesUserPreference = data.filter(
        item =>
            item.notificationPreferredMethodName !== 'None' &&
            item.notificationPreferredMethodId !== 0,
    );

    // Check if the remaining items in the response have a value of 0 for 'count'.
    return mappedMessagesUserPreference.every(
        item => 'count' in item && item.count === 0,
    );
};

const MessagesUserPreference = ({ messagesUserPreferenceData, endDate }) => {
    if (isMessagesUserPreferenceDataEmpty(messagesUserPreferenceData)) {
        return <NotEnoughData />;
    }
    const [chartData, setChartData] = useState([]);
    const formattedAsOfDate = format(
        new Date(endDate),
        DASHBOARD_AS_OF_DATE_FORMAT,
    );
    useEffect(() => {
        if (messagesUserPreferenceData?.length > 0) {
            setChartData(
                mapApiResponseToMessagesUserPreferenceData(
                    messagesUserPreferenceData,
                ),
            );
        }
    }, [messagesUserPreferenceData, endDate]);
    const total = chartData?.reduce((sum, entry) => sum + entry.value, 0);

    return (
        <div className="messages-user-preference">
            <div className="messages-user-preference-date-subtitle">
                {getText('msg_a8770f2d4d18')} {formattedAsOfDate}
            </div>
            <div className="messages-user-preference-chart-wrapper">
                <div className="messages-user-preference-chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (
                                        active &&
                                        payload &&
                                        payload.length > 0
                                    ) {
                                        const { name, value } =
                                            payload[0].payload;
                                        return (
                                            <CustomDashboardTooltip
                                                active={active}
                                                label={name}
                                                value={value}
                                                unit="messages"
                                            />
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Pie
                                data={chartData}
                                innerRadius={75}
                                outerRadius={115}
                                dataKey="value"
                                startAngle={210}
                                endAngle={-150}
                                labelLine={false}
                                label={renderIconLabel}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="messages-user-preference-legend">
                    {chartData.map((entry, index) => {
                        const percentage = (
                            (entry.value / total) *
                            100
                        ).toFixed(1);
                        return (
                            <div
                                key={index}
                                className="messages-user-preference-legend-item"
                            >
                                <div
                                    className="messages-user-preference-legend-dot"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <div className="messages-user-preference-legend-text">
                                    <div className="messages-user-preference-legend-label">
                                        {entry.name}
                                    </div>
                                    <div className="messages-user-preference-legend-value">
                                        {entry.value.toLocaleString()} (
                                        {percentage}%)
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MessagesUserPreference;
